/** PROTOTYPE variant B — grouped Review / Ready / Resolved sections. */

import {
    ACTION_LABEL,
    AmountCell,
    MatchBadge,
    getExtractedLabel,
    getNewAmount,
    getRowStatus,
    rowIsReady,
    rowNeedsReview,
    showCreateButton,
    showIgnoreButton,
    showKeepButton,
    showUpdateButton,
} from "./hrpReconciliationPrototypeShared";

const RowActions = ({ row, onUpdate, onKeep, onIgnore, onCreate, keepPrimary }) => {
    if (row.confirmed || row.skipped) return null;
    const keepBtn = showKeepButton(row) && (
        <button
            type="button"
            className={`btn ${keepPrimary ? "billing-rate-builder__prototype-btn-keep" : ""}`}
            onClick={() => onKeep(row.rowId)}
        >
            Keep HRP
        </button>
    );
    const updateBtn = showUpdateButton(row) && (
        <button type="button" className="btn edit-btn" onClick={() => onUpdate(row.rowId)}>
            Update
        </button>
    );

    return (
        <div className="billing-rate-builder__hrp-reconciliation-actions">
            {keepPrimary ? (
                <>
                    {keepBtn}
                    {updateBtn}
                </>
            ) : (
                <>
                    {updateBtn}
                    {keepBtn}
                </>
            )}
            {showCreateButton(row) && (
                <button type="button" className="btn edit-btn" onClick={() => onCreate(row.rowId)}>
                    Create
                </button>
            )}
            {showIgnoreButton(row) && (
                <button type="button" className="btn" onClick={() => onIgnore(row.rowId)}>
                    Ignore
                </button>
            )}
        </div>
    );
};

const SectionTable = ({ title, subtitle, rows, keepPrimary, ...handlers }) => {
    if (!rows.length) return null;
    return (
        <section className="billing-rate-builder__prototype-section">
            <header className="billing-rate-builder__prototype-section-header">
                <h3>{title}</h3>
                {subtitle && <p>{subtitle}</p>}
            </header>
            <div className="billing-rate-builder__hrp-reconciliation-table-wrap">
                <table className="billing-rate-builder__hrp-reconciliation-table">
                    <thead>
                        <tr>
                            <th>Link type</th>
                            <th>In HRP</th>
                            <th>Extracted</th>
                            <th>New</th>
                            <th>Status</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const extractedLabel = getExtractedLabel(row);
                            const isIgnored = row.skipped || row.userAction === "skip";
                            return (
                                <tr
                                    key={row.rowId}
                                    className={[
                                        row.nameMismatch && !row.confirmed ? "billing-rate-builder__prototype-row--mismatch" : "",
                                        isIgnored ? "billing-rate-builder__prototype-row--ignored" : "",
                                        row.hrpKeepApplied ? "billing-rate-builder__prototype-row--kept" : "",
                                    ].filter(Boolean).join(" ")}
                                >
                                    <td>
                                        <MatchBadge matchType={row.matchType} />
                                        {row.matchScore != null && row.matchType?.includes("fuzzy") && (
                                            <div className="billing-rate-builder__muted">
                                                {Math.round(row.matchScore * 100)}% match
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {row.hrpComponentName ? (
                                            <strong>{row.hrpComponentName}</strong>
                                        ) : (
                                            <span className="billing-rate-builder__muted">—</span>
                                        )}
                                    </td>
                                    <td>
                                        {extractedLabel ? (
                                            <>
                                                <strong>{extractedLabel}</strong>
                                                {row.nameMismatch && !row.hrpKeepApplied && (
                                                    <div className="billing-rate-builder__prototype-warning">Name differs</div>
                                                )}
                                            </>
                                        ) : (
                                            <span className="billing-rate-builder__muted">—</span>
                                        )}
                                    </td>
                                    <td>
                                        <AmountCell amount={getNewAmount(row)} feeType={row.extractedFeeType} />
                                    </td>
                                    <td>
                                        {getRowStatus(row)}
                                        {row.userAction && (
                                            <div className="billing-rate-builder__muted">
                                                {ACTION_LABEL[row.userAction]}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <RowActions row={row} keepPrimary={keepPrimary} {...handlers} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

const HrpReconciliationVariantB = ({ rows, hrpSnapshot, onUpdate, onKeep, onIgnore, onCreate }) => {
    const handlers = { onUpdate, onKeep, onIgnore, onCreate };
    const review = rows.filter(rowNeedsReview);
    const ready = rows.filter(rowIsReady);
    const resolved = rows.filter((r) => r.confirmed || r.skipped);
    const asOfLabel = hrpSnapshot?.asOfDate ? ` as of ${hrpSnapshot.asOfDate}` : "";

    return (
        <div className="billing-rate-builder__alert billing-rate-builder__hrp-reconciliation">
            <div className="billing-rate-builder__page-header">
                <div>
                    <p className="title billing-rate-builder__alert-title">HRP reconciliation{asOfLabel}</p>
                    <p className="billing-rate-builder__alert-body-plain">
                        Work through <strong>Review</strong> rows first (fuzzy or conflicting links). Exact matches are in{" "}
                        <strong>Ready</strong>.
                    </p>
                </div>
            </div>

            <SectionTable
                title={`Review (${review.length})`}
                subtitle="Fuzzy links and HRP+renewal conflicts — Keep HRP is the primary action."
                rows={review}
                keepPrimary
                {...handlers}
            />
            <SectionTable
                title={`Ready (${ready.length})`}
                subtitle="High-confidence matches — Update is the primary action."
                rows={ready}
                keepPrimary={false}
                {...handlers}
            />
            <SectionTable
                title={`Resolved (${resolved.length})`}
                subtitle="Completed decisions."
                rows={resolved}
                keepPrimary={false}
                {...handlers}
            />
        </div>
    );
};

export default HrpReconciliationVariantB;
